import { DbBook, DbChapter } from '@voyager/types';
import { Book, Chapter } from '../types/Book';

/**
 * Get asset URL - handle R2 URLs vs local file paths
 */
function getAssetUrl(assetPath: string): string {
  if (!assetPath) return '';
  
  // If it's already a full URL (R2 URL), return as-is
  if (assetPath.startsWith('http')) {
    return assetPath;
  }
  
  // If it looks like an R2 key path (contains 'covers/', 'audio/', etc.)
  if (assetPath.includes('covers/') || assetPath.includes('audio/') || assetPath.includes('misc/')) {
    // This is an R2 key that needs to be proxied through the API
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787';
    return `${apiBaseUrl}/api/r2/${assetPath}`;
  }
  
  // Legacy local file path - convert to public URL
  if (assetPath.startsWith('/')) {
    return assetPath;
  }
  
  // Relative path - make it absolute from public directory
  return `/${assetPath}`;
}

/**
 * Transform Creator app DbBook to Consumer app Book type
 */
export function transformDbBookToBook(dbBook: DbBook, chapters: DbChapter[] = []): Book {
  return {
    id: dbBook.id,
    title: dbBook.title,
    logline: dbBook.logline || '',
    cover_image: getAssetUrl(dbBook.cover_image || ''),
    estimated_duration: dbBook.estimated_duration || '',
    genre: dbBook.genre || '',
    target_age: dbBook.target_age || '',
    author: 'Independent Creator', // TODO: Get actual author from user table
    author_website: '', // TODO: Add author website field or get from user
    audio_trailer: getAssetUrl(dbBook.audio_trailer || ''),
    
    // Transform chapters
    chapters: chapters.map(transformDbChapterToChapter),
    
    // Legacy fields for compatibility (will be removed later)
    story_paths: [],
    main_paths: [],
    paths: [],
    
    // Preview mode fields (not used in new model)
    preview_mode: false,
    preview_chapters: [],
    feedback_prompt: ''
  };
}

/**
 * Transform Creator app DbChapter to Consumer app Chapter type
 */
export function transformDbChapterToChapter(dbChapter: DbChapter): Chapter {
  return {
    file: `${dbChapter.id}.md`, // Legacy compatibility
    title: dbChapter.title,
    paths: ['all'], // Legacy compatibility - all chapters available to all paths
    audio: getAssetUrl(dbChapter.audio_file || ''),
    illustration: getAssetUrl(dbChapter.illustration || ''),
    status: dbChapter.status,
    
    // Preview mode fields (not used)
    preview_end: false,
    path_name: undefined
  };
}

/**
 * Transform list of DbBooks to Books
 */
export function transformDbBooksToBooks(dbBooks: DbBook[]): Book[] {
  return dbBooks.map(dbBook => transformDbBookToBook(dbBook, []));
}

// Export the function for use elsewhere
export { getAssetUrl };

/**
 * Extract chapter content from database content field
 * The Creator app stores markdown content, Consumer app expects it
 */
export function extractChapterContent(dbChapter: DbChapter): string {
  return dbChapter.content || '';
}

/**
 * Get chapter title from database chapter
 */
export function getChapterTitle(dbChapter: DbChapter): string {
  return dbChapter.title;
}

/**
 * Get choice text for legacy compatibility
 */
export function getChapterChoiceText(dbChapter: DbChapter): string {
  // For now, just return the title as choice text
  // Later we can add choice-specific fields to the database
  return dbChapter.title;
}